import React, { useCallback, useEffect, useState } from "react";
import { ScreenCover } from "../components/ScreenCover.jsx";
import { LightboxNavigationButton } from "./LightboxRoundButton.jsx";
import { LightboxButton } from "./LightboxButton.jsx";
import { useInteractiveProductsSettings } from "../context/interactive-product-context.jsx";
import { useOffScrollOnMount } from "./use-off-scroll-on-mount.js";
import { useSwipe } from "./use-swipe.js";
import { useMoveBySwiping } from "./use-move-by-swiping.js";
import { InteractiveLinkButton } from "../components/InteractiveLinkButton.jsx";
import { LightboxLikeButton } from "./LightboxLikeButton.jsx";
import { webShareAvailable } from "../utils/web-share.js";
import { optionalTranslation } from "../../translations/translation-utils.js";
import {
	APP_BREAKPOINTS,
	useAppBreakpoints,
} from "../hooks/use-app-breakpoints.js";

const LIGHTBOX_MIN_SWIPE_DELTA = 25;

export const ProductLightbox = ({
	products,
	currentProductIndex,
	onClose,
	hasNext,
	hasPrevious,
	moveNext,
	movePrevious,
	lightboxControls,
	webshareLoading,
	share,
}) => {
	const {
		closeBlackIconPath,
		translations,
		shareIconPath,
		disableFollowButton,
		shoppingCartUrl,
		shoppingCartIcon,
	} = useInteractiveProductsSettings();

	const { hideProductLightbox } = lightboxControls;

	const viewedProduct = products[currentProductIndex];

	const { image, buttonLink, buttonText, shareData } = viewedProduct;

	useOffScrollOnMount();

	const handleUnderSwipeThreshold = useCallback(() => {
		hideProductLightbox();
	}, [hideProductLightbox]);

	const { handleSwipe } = useMoveBySwiping({
		minDelta: LIGHTBOX_MIN_SWIPE_DELTA,
		moveLeft: movePrevious,
		moveRight: moveNext,
		onUnderThreshold: handleUnderSwipeThreshold,
	});

	const handleLightboxSwipe = useCallback(
		({ deltaX, deltaY }) => {
			handleSwipe({ deltaX, deltaY });
		},
		[handleSwipe]
	);

	const { handleEnd, handleMove, handleStart, handleWrapperClick } = useSwipe({
		onSwipe: handleLightboxSwipe,
	});

	const { mode } = useAppBreakpoints();

	return (
		<>
			<div className="product-lightbox">
				<div className="product-lightbox__top-section">
					<div className="product-lightbox__top-middle">
						<LightboxButton
							onClick={onClose}
							icon={
								<img
									src={closeBlackIconPath}
									className="product-lightbox__closer"
									alt={optionalTranslation(translations["button.close.icon.alt"])}
								/>
							}
							buttonText={optionalTranslation(translations["close"])}
							title={optionalTranslation(translations["button.close.title"])}
						/>
					</div>

					{hasPrevious && (
						<LightboxNavigationButton direction="left" onClick={movePrevious} />
					)}

					<div className="flex-spacer" />

					{hasNext && (
						<LightboxNavigationButton
							direction="right"
							onClick={moveNext}
							title={optionalTranslation(
								translations["button.lightbox.product.next"]
							)}
						/>
					)}
				</div>

				<div
					className="product-lightbox__middle-section"
					onTouchStart={handleStart}
					onTouchMove={handleMove}
					onTouchEnd={handleEnd}
					// #
					onMouseDown={handleStart}
					onMouseMove={handleMove}
					onMouseUp={handleEnd}
					// #
					onClick={handleWrapperClick}
				>
					<div className="product-lightbox__image">
						<img
							className="product-lightbox__image__contained_img"
							src={image}
							alt={optionalTranslation(translations["product.image.alt"])}
						/>
					</div>
				</div>

				<div className="product-lightbox__bottom-section">
					<InteractiveLinkButton
						buttonLink={buttonLink}
						buttonText={buttonText}
						customLinkClass="product-lightbox__bottom-button-common product-lightbox__bottom-interactive-link-button"
					/>

					{!disableFollowButton ? (
						<LightboxLikeButton
							productUid={viewedProduct.uid}
							followingRequestBody={viewedProduct.followingRequestBody}
						/>
					) : null}

					{shoppingCartUrl ? (
						<InteractiveLinkButton
							buttonText={optionalTranslation(translations["button.cart.text"])}
							title={optionalTranslation(translations["button.cart.title"])}
							icon={<img src={shoppingCartIcon} alt={optionalTranslation(translations["button.cart.icon.alt"])} />}
							buttonLink={shoppingCartUrl}
							customLinkClass="product-lightbox__bottom-button-common product-lightbox_bottom-simple-light-button"
							linkOpenInNew
						/>
					) : null}

					{shareData &&
						mode === APP_BREAKPOINTS.MOBILE &&
						webShareAvailable() && (
							<InteractiveLinkButton
								onClick={() => {
									share(shareData);
								}}
								buttonText={optionalTranslation(translations["share"])}
								customLinkClass="product-lightbox__bottom-button-common product-lightbox_bottom-simple-light-button"
								icon={<img src={shareIconPath} alt={optionalTranslation(translations["button.share.icon.alt"])} />}
								title={optionalTranslation(translations["button.share.title"])}
								disabled={webshareLoading}
							/>
						)}
				</div>
			</div>

			<ScreenCover customColor="#111111c0" />
		</>
	);
};
